/**
 * SCHEDULE'S MONTH VIEW, which no artboard draws, asked for on the owner's walk of 2026-10-16. Drawn
 * in the current tokens and parts, since the visual redesign is still the owner's to choose.
 *
 * - THE MONTH'S NAME between two arrows, each a 48 by 48 button named in words. The view opens on
 *   today's month, the business's day from the place, and the arrows step one month at a time from
 *   the month of the earliest visit in her copy to the month of the last in the horizon
 *   (`monthBounds`, `lib/month.ts`). At either end an arrow stays where it is, says it is not
 *   available, and does nothing, so a thumb or the focus on it is never left on nothing; it is drawn
 *   off, with no box, no fill and no hover and its chevron muted, so it never looks like a day she
 *   could press. When her copy holds this month alone there are no arrows at all, only its name
 *   (the final browser check of 2026-10-17). The name is a polite live region, so a step is heard.
 * - THE GRID: the weeks from Sunday, as Florida's calendars start them, each day of the month a
 *   real button, 44 tall at least, whose name says the day, whether it is today, and its visits in
 *   words (`dayOnTheMonth`); today ringed and marked `aria-current="date"`; the chosen day pressed.
 *   It reads as a grid to a screen reader, rows of cells under the weekdays' names, with one day in
 *   the tab order and the arrow keys, Home and End moving it, never out of the month.
 * - THE MARKS AND THE COLOURS: one mark for each visit on a day, by ops's status, each state a
 *   colour of its own and a shape of its own, so it is never told by colour alone (`MarkIcon`,
 *   `PAINT`), hidden from a screen reader, which hears the day's name. A day's marks are drawn in a
 *   fixed order, under way, planned, could not get in, done, then skipped or cancelled
 *   (`DAY_ORDER`, `lib/month.ts`), and the day is washed in a light tint of the first one's colour,
 *   so the month reads at a glance and a day reads skipped or done only when nothing on it is still
 *   to come (the replay of the owner's walk, 2026-10-17, found a skipped first visit greying a day
 *   whose other homes were still coming). Each mark is
 *   drawn in its own colour on it: planned navy, under way the brand gold, done a sage green,
 *   skipped or cancelled a mid grey and could not get in a warm brown (the tokens and their
 *   contrast are in `styles.css`, held by `Month.test.ts`). The owner asked for the visits colour
 *   coded on the walk of 2026-10-16. The key shows each state's mark in its colour on its wash and
 *   names it in plain words (`MARK_WORDS`), all five always, so it reads the same in every month.
 * - EVERY DAY IN A WEEK IS AS TALL AS THE TALLEST, its box filling its cell, so a day whose marks
 *   wrap onto a second line never hangs below the days beside it.
 * - A CHOSEN DAY is outlined in navy and keeps its wash. Its visits are drawn directly under the
 *   grid, in the copy's order, its day named once over them, each by `card` as the List draws it
 *   less the day, with its home's name as its heading where she sees several homes, and "No
 *   visits this day." for a day with none; the key comes after them. Choosing a day scrolls them
 *   into view only as far as they need, clear of the nav and the phone's own bar at the foot by
 *   their scroll margin, smoothly unless she has asked the device for less motion, so her visits and
 *   their Skip or move are never left under the nav (the final browser check of 2026-10-17 found
 *   them there at 390 by 844 and at 1280 by 800). Before a day is chosen, one line there says to
 *   choose one, or that the month holds no visits. Choosing changes only what is under the grid and
 *   the border's colour, never its width, so nothing moves under her finger.
 * - OPENED ON ONE CLEAN'S DAY, where a copy of the office's text led to it (`opensOn`, slice M1):
 *   that day's month, the day chosen and its visits drawn under the grid, with nothing scrolled
 *   here, since Schedule brings the clean's own card into view.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { ScheduledVisit } from '../api.ts';
import { dayInWords } from '../lib/dates.ts';
import {
  MARKS,
  WEEK_HEADS,
  inDayOrder,
  markOf,
  monthBounds,
  monthInWords,
  monthOf,
  monthWeeks,
  stepWithin,
  type Mark,
} from '../lib/month.ts';
import { MARK_WORDS, SCHEDULE_VIEW, dayOnTheMonth } from '../words.ts';

/** A mark as drawn: a state's, or the plain one for a status this build does not know. */
type Drawn = Mark | 'other';

/**
 * Each state's paint: `ink`, the colour its mark is drawn in, and `wash`, the light tint a day whose
 * first mark in `DAY_ORDER` it is is drawn on. A day with no visit, or whose visits are all in a
 * status this build does not know, is plain white, its mark in the muted ink. Each is a token of
 * `styles.css`, written out whole so the build finds it.
 */
export const PAINT: Readonly<Record<Drawn, { readonly ink: string; readonly wash: string }>> = {
  planned: { ink: 'text-mark-planned', wash: 'bg-wash-planned' },
  underWay: { ink: 'text-mark-under-way', wash: 'bg-wash-under-way' },
  done: { ink: 'text-mark-done', wash: 'bg-wash-done' },
  cancelled: { ink: 'text-mark-cancelled', wash: 'bg-wash-cancelled' },
  noAccess: { ink: 'text-mark-no-access', wash: 'bg-wash-no-access' },
  other: { ink: 'text-text-muted', wash: 'bg-surface-raised' },
};

/** The colour of the day's number, on its wash or on white. */
export const DAY_NUMBER = 'text-text-body';

/** The outline of the chosen day, over its wash. */
export const DAY_CHOSEN_OUTLINE = 'border-surface-brand';

/**
 * Each mark's shape, on a 16 by 16 box: planned a filled dot; under way a ring half filled; done a
 * check; skipped or cancelled a ring struck through; could not get in an open triangle; and a
 * status this build does not know a plain ring.
 */
const SHAPE: Readonly<Record<Drawn, ReactNode>> = {
  planned: <circle cx="8" cy="8" r="6" fill="currentColor" />,
  underWay: (
    <>
      <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 2.75a5.25 5.25 0 0 0 0 10.5Z" fill="currentColor" />
    </>
  ),
  done: (
    <path
      d="M2.75 8.5 6.5 12.25 13.25 4.25"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  cancelled: (
    <>
      <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 12.5 12.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  noAccess: (
    <path
      d="M8 2.25 14.25 13.25H1.75Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  ),
  other: <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.5" />,
};

/** One mark in its state's colour, 14px on a day and 16px in the key, hidden from a screen reader. */
function MarkIcon({ mark, size }: { readonly mark: Drawn; readonly size: 14 | 16 }) {
  return (
    <svg
      data-mark={mark}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${PAINT[mark].ink}`}
    >
      {SHAPE[mark]}
    </svg>
  );
}

/** The chevron on an arrow, pointing the way it steps. */
function Arrow({ toward }: { readonly toward: 'previous' | 'next' }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={toward === 'previous' ? 'M12.5 5 7.5 10 12.5 15' : 'M7.5 5 12.5 10 7.5 15'} />
    </svg>
  );
}

/** Each arrow, on or off, 48 by 48, the project's least tap target (`--spacing-tap`). */
const ARROW = 'flex size-tap shrink-0 items-center justify-center rounded-control border-2';
const ARROW_ON =
  'border-border-brand bg-surface-raised text-text-brand hover:bg-surface-accent-soft';
/** An arrow that cannot step: no box, no fill, no hover, and the chevron muted. */
const ARROW_OFF = 'cursor-not-allowed border-transparent bg-transparent text-border-strong';

/**
 * A day: at least 44 tall, and as tall as its cell, which the week's row stretches to its tallest
 * day, so a day whose marks wrap onto a second line never hangs below the rest of its week (the
 * replay of the owner's walk, 2026-10-17, measured 16 px at 390).
 */
const DAY = `flex h-full min-h-11 w-full flex-col items-center gap-0.5 rounded-control border-2 px-0.5 pt-1 pb-1.5 text-small ${DAY_NUMBER}`;
const DAY_CHOSEN = `${DAY_CHOSEN_OUTLINE} font-semibold`;
/** A day with visits, on its wash: no border of its own, until a pointer is over it. */
const DAY_WASHED = 'border-transparent hover:border-border-strong';
/** A day with none, plain white in the cards' own hairline. */
const DAY_PLAIN = 'border-border-subtle hover:border-border-strong';

/** The card drawn for a chosen day with no visit on it, as a visit's card is drawn. */
const CARD = 'rounded-card border border-border-subtle bg-surface-raised px-5 py-4';

/**
 * The chosen day's visits, as they scroll into view: a little room above, and below them 96 for the
 * nav, which the browser draws 72 tall at 390 and at 1280, and the phone's own bar at the foot, so
 * the last of them, and its Skip or move, stand clear of both. A day whose visits are taller than
 * the screen is brought to its top instead, its name and first card first (`block: 'nearest'`).
 */
const PANEL = 'flex scroll-mt-4 scroll-mb-[calc(6rem+env(safe-area-inset-bottom))] flex-col gap-2';

/** Whether she has asked the device for less motion, where it can say. */
function lessMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The keys that move the focus among the days, and how far along the month each moves it. */
const STEPS: Readonly<Record<string, number>> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

interface MonthProps {
  /** The business's day, from the place. */
  readonly today: string;
  /** Every visit the place answers, before today and from it, in the copy's order. */
  readonly visits: readonly ScheduledVisit[];
  /** One visit, drawn as the List draws it, less its day, which is named once over the day's visits. */
  readonly card: (visit: ScheduledVisit) => ReactNode;
  /**
   * A day to open on, its month shown and the day chosen, as a link to one clean asks (Schedule's
   * `?visit=`, slice M1); without one, or with a day that is not real, today's month and nothing
   * chosen. Read when the month is first drawn, and never again.
   */
  readonly opensOn?: string | undefined;
}

export function Month({ today, visits, card, opensOn }: MonthProps) {
  const headingId = useId();
  const byDay = useMemo(() => {
    const held = new Map<string, ScheduledVisit[]>();
    for (const visit of visits) {
      if (visit.date === undefined) continue;
      const day = held.get(visit.date);
      if (day) day.push(visit);
      else held.set(visit.date, [visit]);
    }
    return held;
  }, [visits]);
  const bounds = useMemo(() => monthBounds([...byDay.keys()], today), [byDay, today]);
  /** The day it opens on, where one was asked for and is a real day. */
  const [opening] = useState(() =>
    opensOn !== undefined && monthOf(opensOn) !== null ? opensOn : null,
  );
  const [shown, setShown] = useState(() => monthOf(opening ?? today));
  const [chosen, setChosen] = useState<string | null>(opening);
  /** The day that holds the grid's one place in the tab order, once she has moved it. */
  const [roving, setRoving] = useState<string | null>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const panel = useRef<HTMLDivElement>(null);
  /** How many times she has chosen a day, the same one again included: each brings it into view. */
  const [choices, setChoices] = useState(0);

  useEffect(() => {
    if (choices === 0) return;
    panel.current?.scrollIntoView?.({
      block: 'nearest',
      behavior: lessMotion() ? 'auto' : 'smooth',
    });
  }, [choices]);

  if (bounds === null || shown === null) return null;
  // A read again may have moved the bounds: the month shown stays inside them.
  const month = shown < bounds.first ? bounds.first : shown > bounds.last ? bounds.last : shown;
  const weeks = monthWeeks(month);
  const days = weeks.flat().filter((day): day is string => day !== null);
  const here = (day: string | null): day is string => day !== null && day.slice(0, 7) === month;
  const stop = [roving, chosen, today].find(here) ?? days[0];
  const previous = stepWithin(month, -1, bounds);
  const next = stepWithin(month, 1, bounds);
  const chosenHere = here(chosen) ? chosen : null;
  const onChosen = chosenHere === null ? [] : (byDay.get(chosenHere) ?? []);

  /** Moves the focus from a day, as the key pressed says, and never out of the month. */
  const move = (event: KeyboardEvent<HTMLButtonElement>, from: string) => {
    let to: string | undefined;
    if (event.key in STEPS) {
      to = days[days.indexOf(from) + STEPS[event.key]!];
    } else if (event.key === 'Home' || event.key === 'End') {
      const week = weeks.find((each) => each.includes(from))!.filter((day) => day !== null);
      to = event.key === 'Home' ? week[0] : week.at(-1);
    } else {
      return;
    }
    event.preventDefault();
    if (to === undefined) return;
    setRoving(to);
    buttons.current.get(to)?.focus();
  };

  const choose = (day: string) => {
    setChosen(day);
    setRoving(day);
    setChoices((count) => count + 1);
  };
  /** Whether the arrows have anywhere to step: not when her copy holds this month alone. */
  const steps = bounds.first !== bounds.last;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex min-h-tap items-center justify-between gap-2">
        {steps ? (
          <button
            type="button"
            aria-label={SCHEDULE_VIEW.previous}
            aria-disabled={previous === null}
            onClick={() => previous !== null && setShown(previous)}
            className={`${ARROW} ${previous === null ? ARROW_OFF : ARROW_ON}`}
          >
            <Arrow toward="previous" />
          </button>
        ) : null}
        <h2
          id={headingId}
          aria-live="polite"
          className="flex-1 text-center font-serif text-h3 font-semibold text-text-body"
        >
          {monthInWords(month)}
        </h2>
        {steps ? (
          <button
            type="button"
            aria-label={SCHEDULE_VIEW.next}
            aria-disabled={next === null}
            onClick={() => next !== null && setShown(next)}
            className={`${ARROW} ${next === null ? ARROW_OFF : ARROW_ON}`}
          >
            <Arrow toward="next" />
          </button>
        ) : null}
      </div>

      <div role="grid" aria-labelledby={headingId} className="flex flex-col gap-1">
        <div role="row" className="grid grid-cols-7 gap-1">
          {WEEK_HEADS.map((head) => (
            <div
              key={head.long}
              role="columnheader"
              className="text-center text-small text-text-muted"
            >
              <span aria-hidden="true">{head.short}</span>
              <span className="sr-only">{head.long}</span>
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div
            key={`${month}-${week.find((day) => day !== null)}`}
            role="row"
            className="grid grid-cols-7 gap-1"
          >
            {week.map((day, column) => {
              if (day === null) return <div key={`blank-${column}`} role="gridcell" />;
              const on = byDay.get(day) ?? [];
              // In the day's fixed order, what is under way or still to come first.
              const marks = inDayOrder(on.map((visit) => markOf(visit.status)));
              const isToday = day === today;
              const isChosen = day === chosenHere;
              // Washed in the colour of its first mark in that order, unless no state is known.
              const first = marks[0] ?? null;
              const fill = first === null ? PAINT.other.wash : PAINT[first].wash;
              const edge = isChosen ? DAY_CHOSEN : first === null ? DAY_PLAIN : DAY_WASHED;
              return (
                <div key={day} role="gridcell">
                  <button
                    ref={(button) => {
                      if (button) buttons.current.set(day, button);
                      else buttons.current.delete(day);
                    }}
                    type="button"
                    data-day={day}
                    aria-label={dayOnTheMonth(dayInWords(day) ?? day, marks, isToday)}
                    aria-pressed={isChosen}
                    aria-current={isToday ? 'date' : undefined}
                    tabIndex={day === stop ? 0 : -1}
                    onClick={() => choose(day)}
                    onKeyDown={(event) => move(event, day)}
                    className={`${DAY} ${fill} ${edge}`}
                  >
                    <span
                      className={`flex size-7 items-center justify-center rounded-full border-2 ${
                        isToday ? 'border-surface-brand font-semibold' : 'border-transparent'
                      }`}
                    >
                      {Number(day.slice(8))}
                    </span>
                    <span
                      aria-hidden="true"
                      className="flex min-h-3.5 flex-wrap justify-center gap-0.5"
                    >
                      {marks.map((mark, index) => (
                        <MarkIcon key={index} mark={mark ?? 'other'} size={14} />
                      ))}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div ref={panel} data-day-panel className={PANEL}>
        {chosenHere === null ? (
          <p className="text-body text-pretty text-text-muted">
            {days.some((day) => byDay.has(day))
              ? SCHEDULE_VIEW.chooseADay
              : SCHEDULE_VIEW.noVisitsThisMonth}
          </p>
        ) : (
          <>
            <h2 className="text-body font-semibold text-text-body">{dayInWords(chosenHere)}</h2>
            {onChosen.length === 0 ? (
              <div className={CARD}>
                <p className="text-small text-text-body">{SCHEDULE_VIEW.noVisitsThisDay}</p>
              </div>
            ) : (
              <ol className="flex flex-col gap-3">
                {onChosen.map((visit) => (
                  <li key={visit.id}>{card(visit)}</li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>

      <ul data-key className="flex flex-wrap gap-x-5 gap-y-2 text-small text-text-body">
        {MARKS.map((mark) => (
          <li key={mark} className="flex items-center gap-2">
            <span
              data-swatch
              aria-hidden="true"
              className={`flex size-7 shrink-0 items-center justify-center rounded-control ${PAINT[mark].wash}`}
            >
              <MarkIcon mark={mark} size={16} />
            </span>
            {MARK_WORDS[mark]}
          </li>
        ))}
      </ul>
    </div>
  );
}
